import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import {
  Button,
  Spinner,
  Table,
  TableColumnProps,
  TableRow,
  TextInput,
} from "north.js";
import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { useClipboard } from "use-clipboard-copy";

import { PageBar } from "src/shared/components";
import { successToast } from "src/shared/components/Toast";

import { Volunteer } from "../index";

import RoleLabels from "./RoleLabels";

const TABLE_COLUMNS: TableColumnProps[] = [
  { id: "name", name: "NAME" },
  { id: "email", name: "EMAIL" },
  { id: "roles", name: "ROLES" },
  { id: "shiftsSignedUp", name: "SHIFTS SIGNED UP", align: "center" },
  { id: "shiftsCompleted", name: "SHIFTS COMPLETED", align: "center" },
  { id: "hoursCompleted", name: "HOURS COMPLETED", align: "center" },
  { id: "view", name: "VIEW", align: "center", hideLabel: true },
];

interface VolunteersTableProps {
  setViewVolunteer: (volunteer: Volunteer) => void;
  volunteers: Volunteer[];
  loading: boolean;
}

const VolunteersTable = ({
  setViewVolunteer,
  volunteers,
  loading,
}: VolunteersTableProps) => {
  const [query, setQuery] = useState<string>("");

  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const clipboard = useClipboard();

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const tableData: TableRow[] = volunteers
    .filter(
      ({ name, email }: Volunteer) =>
        name.includes(query) || email.includes(query)
    )
    .map((volunteer: Volunteer) => {
      const shiftsCompleted = volunteer.shifts.filter(
        ({ checkedIn, endTime }) => checkedIn && endTime < new Date()
      );

      return {
        name: volunteer.name,
        email: (
          <button
            data-tip="Click to copy"
            onClick={() => {
              clipboard.copy(volunteer.email);
              successToast("Copied email to clipboard!");
            }}
          >
            {volunteer.email}
          </button>
        ),
        roles: (
          <RoleLabels
            key={`volunteer-roles-${volunteer.eventAttendeeId}`}
            roles={[volunteer.primaryRole, ...volunteer.additionalRoles]}
            numShown={2}
          />
        ),
        shiftsSignedUp: volunteer.shifts.length,
        shiftsCompleted: shiftsCompleted.length,
        hoursCompleted: volunteer.hoursCompleted,
        view: (
          <Button
            key={`view-${volunteer.eventAttendeeId}`}
            color="secondary"
            onClick={() => setViewVolunteer(volunteer)}
          >
            View
          </Button>
        ),
      };
    });

  return loading ? (
    <Spinner />
  ) : (
    <>
      <SearchContainer>
        <TextInput
          type="text"
          placeholder="Search for a volunteer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          leadingIcon={<MagnifyingGlassIcon width="16px" height="16px" />}
        />
      </SearchContainer>
      <Table
        columns={TABLE_COLUMNS}
        data={tableData}
        displayStyle="striped"
        showFooter
        horizontalPadding={16}
        verticalPadding={8}
        itemsPerPage={itemsPerPage}
        page={page}
      />
      <PageBar
        page={page}
        itemsPerPage={itemsPerPage}
        totalItems={tableData.length}
        onPageChange={setPage}
        onItemsPerPageChange={setItemsPerPage}
      />
      <StyledTooltip place="top" effect="solid" />
    </>
  );
};

export default VolunteersTable;

const SearchContainer = styled.div`
  margin-bottom: 32px;
`;

const StyledTooltip = styled(ReactTooltip)`
  border-radius: 10px !important;
  background: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease-out, visibility 0.3s ease-out !important;

  &.__react_component_tooltip.show {
    opacity: 0.75 !important;
  }
`;
